import type { CacheConfig } from 'http'

interface SetCacheConfig<Response> extends CacheConfig {
  key: string
  result?: Response
}

/**
 * Сервис для кэширования запросов
 */
export class CacheService {
  private $cacheStore: any
  private readonly dirName: string

  constructor(cachedStore: any) {
    this.$cacheStore = cachedStore
    this.dirName = `./cachedData`
  }

  /**
   * Возвращает cachedRequests из стора
   */
  private get cachedRequests() {
    return this.$cacheStore.cachedRequests
  }

  /**
   * устанваливает запись кэша
   * @param config
   */
  public async setCacheItem<Response>(config: SetCacheConfig<Response>) {
    const { key, client, server } = config

    // записываем для клиентского кэширования всегда, чтобы не перезапрашивать
    this.setClientItem(key, client?.lifeTime)

    if (server) {
      await this.setServerItem(config)
    }
  }

  /**
   * возвращает закэшированные данные
   */
  public async getCacheItem<Response>(config: SetCacheConfig<Response>) {
    const { key, client } = config

    if (!(await this.isCacheValid(key))) return

    if (process.client) {
      return await this.getClientItem()
    } else {
      // записываем для клиентского кэширования всегда, чтобы не перезапрашивать на клиенте
      this.setClientItem(key, client?.lifeTime)

      return await this.getServerItem(key)
    }
  }

  /**
   * проверяет валидность кэша
   * @param key
   */
  public async isCacheValid(key: string): Promise<boolean> {
    if (process.client) {
      return this.isClientCacheValid(key)
    } else {
      return await this.isServerCacheValid(key)
    }
  }

  /**
   * устанавливает время жизни кэша
   * @param lifeTime - время жизни в миллисекундах
   * @private
   */
  private setCacheLifeTime(lifeTime?: number) {
    if (!lifeTime && lifeTime !== 0)
      return Date.now() + getMilliseconds.inHours(1) // 1 час

    return Date.now() + lifeTime
  }

  /**
   * Метод проверяет, актуальны ли еще данные по совершенному ранее запросу
   * @param {string} url - url запроса, по которому необходимо проверить данные
   * @returns {boolean} - true, если запрос ранее совершался и время expiresAt
   * еще не наступило. False - если запрос не найден в списке cachedRequests или срок
   * актуальности данных уже истек
   */
  private isClientCacheValid(url: string): boolean {
    const item = this.cachedRequests[url]

    if (!item) {
      return false
    }

    if (Date.now() > item) {
      this.$cacheStore.deleteCache(url)

      return false
    }

    return true
  }

  /**
   * проверяет установленное время жизни файла
   * @param key
   */
  private async isServerCacheValid(key: string) {
    return !!(await $fetch('/api/cache/check', { params: { key } }))
  }

  /**
   * Метод сохраняет данные по совершенному запросу с определенным сроком актуальности
   * @param {string} url - url запроса, по которому сохраняем данные
   * @param {number} lifeTime? - время в миллисекундах, в течение которого полученые по запросу
   * данные будут оставаться актуальными, по умолчанию 1 час (3600000 мс)
   */
  private setClientItem(url: string, lifeTime?: number): void {
    this.$cacheStore.set({
      url,
      item: this.setCacheLifeTime(lifeTime), // 1 час по умолчанию,
    })
  }

  /**
   * возвращаем пустой ответ,
   * тк кэш ещё валиден. данные есть на клиенте
   * и запрос не надо отправлять
   * @private
   */
  private async getClientItem() {
    return await new Promise((resolve) => resolve({}))
  }

  /**
   * устанавливаем серверный кэш
   * @param config
   */
  private async setServerItem<Response>(
    config: SetCacheConfig<Response>,
  ): Promise<void> {
    const { key, result, server } = config

    if (!process.server || !server) return

    try {
      await $fetch('/api/cache', {
        method: 'post',
        body: {
          key,
          lifeTime: server.lifeTime,
          data: result,
        },
      })
    } catch (e) {
      console.log('error create cache ', e)
    }
  }

  /**
   * получаем закэшированный файл
   * @param key
   */
  private async getServerItem(key: string) {
    if (!process.server) return

    // здесь мы уверены, что кэш всегда существует
    // тк проверили что кэш валиден
    return await $fetch('/api/cache', { params: { key } })
  }
}
